/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
// @ts-ignore
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, EditorState, convertFromHTML, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import AUIInputTextField from '@ancert/aui-input-text-field';
import AUIInputUploadBasic from '@ancert/aui-input-upload-basic';
import AUIField from '@ancert/aui-field';
import AUIButton from '@ancert/aui-button';
import { useNotification } from '@ancert/aui-notification-context';
import { useForm } from 'react-hook-form';
import useUploadFile from '../../../hooks/useUploadFile';
import { defaultValues, resolver } from './form';
import CONFIG_KEY from '../../../constants/config.constants';
import {
  getConditions,
  getCookies,
  getInstance,
  getManual,
  getPrivacy,
  putConfig,
} from '../../../services/config.service';
import { useTheme } from '../../../context/theme.context';
import { MANUAL_NAME, MAX_FILE_SIZE, MAX_IMAGE_SIZE, POLICY_NAMES } from '../../../config';
import { getFileFromUrl } from '../../../utils/fileUtils';

const Configuracion = () => {
  const { files, register, addPreloadFileNames, removeFile } = useUploadFile({
    mode: 'onSubmit',
  });
  const { control, formState, handleSubmit, reset, setValue } = useForm({
    defaultValues,
    resolver,
    mode: 'onSubmit',
  });
  const [file, setFile] = useState(null);
  const [previousInfo, setPreviousInfo] = useState({
    primaryColor: '',
    secondaryColor: '',
    file: undefined,
    title: '',
    body: '',
    legal: '',
  });
  const { openNotification } = useNotification();
  const { loadTheme } = useTheme();
  const [textEditorState, setTextEditorState] = useState(() => EditorState.createEmpty());
  const [legalEditorState, setLegalEditorState] = useState(() => EditorState.createEmpty());
  const [textBody, setTextBody] = useState('');
  const [legalText, setLegalText] = useState('');
  const errorModal = (message) => {
    openNotification({
      title: 'Error',
      content: <div>{message || 'Ha ocurrido un error al aplicar los cambios'}</div>,
      type: 'error',
      centerButtons: true,
    });
  };

  const getContentAsHtml = (editorState) => {
    const contentState = editorState.getCurrentContent();
    const rawContentState = convertToRaw(contentState);
    return draftToHtml(rawContentState);
  };

  const updateTextDescription = (state) => {
    setTextEditorState(state);
    setTextBody(getContentAsHtml(textEditorState));
  };

  const updateLegalText = (state) => {
    setLegalEditorState(state);
    setLegalText(getContentAsHtml(legalEditorState));
  };

  const getInstanceData = async () => {
    const { status, data: result, error } = await getInstance();
    if (status === 200) {
      const tema = result.find(({ key }) => key === CONFIG_KEY.TEMA);
      const { mainImage, mainImageType, mainImageName, primaryColor, secondaryColor } = JSON.parse(tema.value);
      setValue('primaryColor', primaryColor);
      setValue('secondaryColor', secondaryColor);
      const logoFile = { rawFile: { name: mainImageName }, returnBase64: `${mainImageType},${mainImage}` };
      if (mainImageType && mainImage) {
        setFile(logoFile);
      }

      const texto = result.find(({ key }) => key === CONFIG_KEY.TEXTO);
      const { title, body } = JSON.parse(texto.value);
      setValue('tituloWeb', title);
      setTextEditorState(EditorState.createWithContent(ContentState.createFromBlockArray(convertFromHTML(body))));
      setTextBody(body);
      const legal = result.find(({ key }) => key === CONFIG_KEY.LEGAL);
      setLegalEditorState(
        EditorState.createWithContent(ContentState.createFromBlockArray(convertFromHTML(legal?.value)))
      );
      setLegalText(legal?.value);
      setPreviousInfo({
        ...previousInfo,
        primaryColor,
        secondaryColor,
        file: { rawFile: { name: mainImageName }, returnBase64: `${mainImageType},${mainImage}` },
        title,
        body,
        legal: legal?.value,
      });
      return logoFile;
    }
    const { message } = error;
    errorModal(message || error || 'Ha ocurrido error al cargar los datos');
    return null;
  };

  const getPrivacyFile = async () => {
    const doc = await getFileFromUrl(getPrivacy);
    return doc;
  };

  const getConditionsFile = async () => {
    const doc = await getFileFromUrl(getConditions);
    return doc;
  };

  const getCookiesFile = async () => {
    const doc = await getFileFromUrl(getCookies);
    return doc;
  };

  const getUserManualFile = async () => {
    const doc = await getFileFromUrl(getManual);
    return doc;
  };

  /**
   *
   * @param {string} preloadFile
   * @returns
   */
  const isFileLoaded = (preloadFile) => !!preloadFile.split(',')[1];

  const getAllData = async () => {
    try {
      const results = await Promise.all([
        getInstanceData(),
        getConditionsFile(),
        getPrivacyFile(),
        getCookiesFile(),
        getUserManualFile(),
      ]);

      addPreloadFileNames({
        organizacionLogo: results[0].rawFile?.name,
        politicaSii: isFileLoaded(results[1]) ? POLICY_NAMES.INFORMATION : '',
        politicaPrivacidad: isFileLoaded(results[2]) ? POLICY_NAMES.PRIVACY : '',
        politicaCookies: isFileLoaded(results[3]) ? POLICY_NAMES.COOKIES : '',
        manualUsuarios: isFileLoaded(results[4]) ? MANUAL_NAME : '',
      });
      const error = results.find((res) => !res);
      if (error) {
        errorModal('Ha ocurrido un error al cargar los datos');
      }
    } catch (error) {
      const { message } = error;
      errorModal(message || error || 'Ha ocurrido un error al cargar los datos');
    }
  };

  useEffect(() => {
    getAllData();
  }, []);

  const FICHEROS_VALIDOS_LOGO = {
    description: 'png, jpg',
    filter: ['.png', '.jpg'],
  };

  const FICHEROS_VALIDOS_POLITICS = {
    description: 'pdf',
    filter: ['.pdf'],
  };

  const discardChanges = () => {
    reset(defaultValues);
    removeFile('organizacionLogo', true);
    removeFile('politicaSii', true);
    removeFile('politicaCookies', true);
    removeFile('politicaPrivacidad', true);
    removeFile('manualUsuarios', true);
    getAllData();
  };

  const saveTheme = async ({ primaryColor, secondaryColor }) => {
    let fileFormat = ['', ''];
    const valueTema = {
      primaryColor,
      secondaryColor,
    };

    let fileToSend = null;
    if (files.organizacionLogo.preloadFileName || files.organizacionLogo.file) {
      fileToSend = files.organizacionLogo.file ?? previousInfo.file;
    }

    if (fileToSend) {
      fileFormat = fileToSend.returnBase64.split(',');
      const [mainImageType, mainImage] = fileFormat;
      valueTema.mainImageType = mainImageType;
      valueTema.mainImage = mainImage;
      valueTema.mainImageName = fileToSend?.rawFile?.name;
    } else {
      valueTema.mainImageType = '';
      valueTema.mainImage = '';
      valueTema.mainImageName = '';
    }
    const dataTema = {
      key: CONFIG_KEY.TEMA,
      value: JSON.stringify(valueTema),
    };

    const response = await putConfig({ data: dataTema });
    return response;
  };

  const saveText = async ({ tituloWeb }) => {
    const valueText = {
      title: tituloWeb,
    };
    valueText.body = textBody === '<p></p>\n' ? previousInfo.body : textBody;
    const dataText = {
      key: CONFIG_KEY.TEXTO,
      value: JSON.stringify(valueText),
    };

    const response = await putConfig({ data: dataText });
    return response;
  };

  const saveLegal = async () => {
    const dataText = {
      key: CONFIG_KEY.LEGAL,
      value: legalText === '<p></p>\n' ? previousInfo.legal : legalText,
    };

    const response = await putConfig({ data: dataText });
    return response;
  };

  const saveConditions = async () => {
    let fileToSend = null;
    if (files?.politicaSii?.file) {
      fileToSend = files.politicaSii.file.returnBase64;
    }

    const dataText = {
      key: CONFIG_KEY.POLITICA_SII,
      value: fileToSend ? fileToSend.split(',')[1] : '',
    };

    const response = await putConfig({ data: dataText });
    return response;
  };
  const savePrivacy = async () => {
    let fileToSend = null;
    if (files?.politicaPrivacidad?.file) {
      fileToSend = files.politicaPrivacidad.file.returnBase64;
    }

    const dataText = {
      key: CONFIG_KEY.POLITICA_PRIV,
      value: fileToSend ? fileToSend.split(',')[1] : '',
    };

    const response = await putConfig({ data: dataText });
    return response;
  };

  const saveCookies = async () => {
    let fileToSend = null;
    if (files?.politicaCookies?.file) {
      fileToSend = files.politicaCookies.file.returnBase64;
    }

    const dataText = {
      key: CONFIG_KEY.POLITICA_COOKIES,
      value: fileToSend ? fileToSend.split(',')[1] : '',
    };

    const response = await putConfig({ data: dataText });
    return response;
  };

  const saveManual = async () => {
    let fileToSend = null;
    if (files?.manualUsuarios?.file) {
      fileToSend = files.manualUsuarios.file.returnBase64;
    }

    const dataText = {
      key: CONFIG_KEY.MANUAL_USUARIOS,
      value: fileToSend ? fileToSend.split(',')[1] : '',
    };

    const response = await putConfig({ data: dataText });
    return response;
  };

  const makeAllApiCalls = async (form) => {
    const { primaryColor, secondaryColor, tituloWeb } = form;
    try {
      const promises = [];
      if (primaryColor || secondaryColor || previousInfo) {
        promises.push(
          saveTheme({
            primaryColor: primaryColor || previousInfo?.primaryColor,
            secondaryColor: secondaryColor || previousInfo?.secondaryColor,
          })
        );
      }
      if (tituloWeb || previousInfo?.title) {
        promises.push(saveText({ tituloWeb: tituloWeb || previousInfo?.title }));
      }
      if (legalText || previousInfo?.legal) {
        promises.push(saveLegal());
      }

      if (!(files?.politicaSii?.preloadFileName && !files.politicaSii?.file)) {
        promises.push(saveConditions());
      }
      if (!(files?.politicaCookies?.preloadFileName && !files.politicaCookies?.file)) {
        promises.push(saveCookies());
      }
      if (!(files?.politicaPrivacidad?.preloadFileName && !files.politicaPrivacidad?.file)) {
        promises.push(savePrivacy());
      }
      if (!(files?.manualUsuarios?.preloadFileName && !files.manualUsuarios?.file)) {
        promises.push(saveManual());
      }
      const results = await Promise.all(promises);
      if (results.every((result) => result.status === 200)) {
        loadTheme(true);
      }
    } catch (error) {
      errorModal(error);
    }
  };

  const onSubmit = (form) => {
    makeAllApiCalls(form);
  };

  return (
    <div>
      <h1 className="u-mt-24">Configuración</h1>
      <form autoComplete="off">
        <div className="o-grid">
          <div className="o-grid__row">
            <div className="o-grid__col-6">
              <AUIInputTextField
                name="tituloWeb"
                label="Título de la web"
                controlRef={control}
                errors={formState.errors}
              />
            </div>
          </div>
          <div className="o-grid__row">
            <div className="o-grid__col-6">
              <p className="c-sii-config__label">Descripción en pantalla de inicio</p>
              <Editor
                editorState={textEditorState}
                toolbarClassName="c-sii-config__toolbar"
                editorClassName="c-sii-config__editor"
                onEditorStateChange={updateTextDescription}
                toolbar={{
                  options: ['inline', 'textAlign', 'list'],
                  inline: {
                    options: ['bold', 'italic', 'underline'],
                    bold: { className: 'bordered-option-classname' },
                    italic: { className: 'bordered-option-classname' },
                    underline: { className: 'bordered-option-classname' },
                  },
                  textAlign: {
                    options: ['left', 'center', 'right', 'justify'],
                  },
                  list: {
                    options: ['unordered'],
                    unordered: { className: undefined },
                  },
                }}
              />
            </div>
          </div>
          <div className="o-grid__row u-mt-32">
            <div className="o-grid__col-6">
              <AUIInputUploadBasic
                label="Logotipo organización"
                returnBase64
                maxFileSize={MAX_IMAGE_SIZE}
                {...register({
                  name: 'organizacionLogo',
                  accept: FICHEROS_VALIDOS_LOGO,
                })}
              />
            </div>
          </div>
          {(files.organizacionLogo?.file || (file?.returnBase64 && files.organizacionLogo?.preloadFileName)) && (
            <div className="o-grid__row">
              <div className="o-grid__col-6">
                <AUIField label="Previsualización">
                  <img
                    src={files.organizacionLogo?.file?.returnBase64 ?? file?.returnBase64}
                    alt="Logo de la empresa"
                    className="c-sii-preview"
                  />
                </AUIField>
              </div>
            </div>
          )}
          <div className="o-grid__row">
            <div className="o-grid__col-6">
              <p className="c-sii-config__label">Texto legal</p>
              <Editor
                editorState={legalEditorState}
                toolbarClassName="c-sii-config__toolbar"
                editorClassName="c-sii-config__editor"
                onEditorStateChange={updateLegalText}
                toolbar={{
                  options: ['inline', 'textAlign', 'list'],
                  inline: {
                    options: ['bold', 'italic', 'underline'],
                    bold: { className: 'bordered-option-classname' },
                    italic: { className: 'bordered-option-classname' },
                    underline: { className: 'bordered-option-classname' },
                  },
                  textAlign: {
                    options: ['left', 'center', 'right', 'justify'],
                  },
                  list: {
                    options: ['unordered'],
                    unordered: { className: undefined },
                  },
                }}
              />
            </div>
          </div>
          <div className="o-grid__row  u-mt-32">
            <div className="o-grid__col-3">
              <AUIInputTextField
                name="primaryColor"
                label="Color primario"
                controlRef={control}
                errors={formState.errors}
              />
            </div>
            <div className="o-grid__col-3">
              <AUIInputTextField
                name="secondaryColor"
                label="Color secundario"
                controlRef={control}
                errors={formState.errors}
              />
            </div>
          </div>
          <div className="o-grid__row">
            <div className="o-grid__col-6">
              <AUIInputUploadBasic
                label="Política sistema interno de información"
                returnBase64
                maxFileSize={MAX_FILE_SIZE}
                {...register({
                  name: 'politicaSii',
                  accept: FICHEROS_VALIDOS_POLITICS,
                })}
              />
            </div>
          </div>
          <div className="o-grid__row">
            <div className="o-grid__col-6">
              <AUIInputUploadBasic
                label="Política de privacidad"
                returnBase64
                maxFileSize={MAX_FILE_SIZE}
                {...register({
                  name: 'politicaPrivacidad',
                  accept: FICHEROS_VALIDOS_POLITICS,
                })}
              />
            </div>
          </div>
          <div className="o-grid__row">
            <div className="o-grid__col-6">
              <AUIInputUploadBasic
                label="Política de cookies"
                returnBase64
                maxFileSize={MAX_FILE_SIZE}
                {...register({
                  name: 'politicaCookies',
                  accept: FICHEROS_VALIDOS_POLITICS,
                })}
              />
            </div>
          </div>
          <div className="o-grid__row">
            <div className="o-grid__col-6">
              <AUIInputUploadBasic
                label="Manual de usuario"
                returnBase64
                maxFileSize={MAX_FILE_SIZE}
                {...register({
                  name: 'manualUsuarios',
                  accept: FICHEROS_VALIDOS_POLITICS,
                })}
              />
            </div>
          </div>
        </div>
        <div className="o-sii-main-container__buttons">
          <AUIButton mode="tertiary" label="Descarta cambios" size="lg" onClick={() => discardChanges()} />
          <AUIButton type="submit" label="Guardar cambios" size="lg" onClick={handleSubmit(onSubmit)} />
        </div>
      </form>
    </div>
  );
};

Configuracion.propTypes = {};

export default Configuracion;
